import { supabase } from '../lib/supabase';
import { getChatCompletion } from './aiIntegrations/chatCompletion';
import { convertKeysToCamelCase } from './supabaseService';

const AI_PROVIDER = 'OPEN_AI';
const AI_MODEL = 'gpt-5';

/**
 * Fetch comprehensive business data for AI context
 */
export async function fetchBusinessContext() {
  try {
    const [inventory, tenants, leads, centers] = await Promise.all([
      supabase?.from('inventory')?.select('*'),
      supabase?.from('tenants')?.select('*'),
      supabase?.from('leads')?.select('*'),
      supabase?.from('centers')?.select('*')
    ]);

    return {
      inventory: convertKeysToCamelCase(inventory?.data || []),
      tenants: convertKeysToCamelCase(tenants?.data || []),
      leads: convertKeysToCamelCase(leads?.data || []),
      centers: convertKeysToCamelCase(centers?.data || [])
    };
  } catch (error) {
    console.error('Fetch business context error:', error);
    throw error;
  }
}

/**
 * Calculate key metrics from business data
 */
export function calculateMetrics(data) {
  const { inventory, tenants, leads, centers } = data;

  // Occupancy metrics
  const totalUnits = inventory?.length || 0;
  const occupiedUnits = inventory?.filter(item => item?.status === 'occupied')?.length || 0;
  const vacantUnits = inventory?.filter(item => item?.status === 'vacant')?.length || 0;
  const occupancyRate = totalUnits > 0 ? ((occupiedUnits / totalUnits) * 100)?.toFixed(1) : 0;

  // Revenue metrics
  const totalRevenue = inventory?.reduce((sum, item) => sum + (item?.rentAmount || 0), 0);
  const avgRentPerSqft = inventory?.length > 0 
    ? (totalRevenue / inventory?.reduce((sum, item) => sum + (item?.sqft || 0), 0))?.toFixed(2)
    : 0;

  // Tenant metrics
  const activeTenants = tenants?.filter(t => t?.status === 'active')?.length || 0;
  const expiringLeases = tenants?.filter(t => {
    if (!t?.leaseEndDate) return false;
    const endDate = new Date(t?.leaseEndDate);
    const threeMonthsFromNow = new Date();
    threeMonthsFromNow?.setMonth(threeMonthsFromNow?.getMonth() + 3);
    return endDate <= threeMonthsFromNow && endDate >= new Date();
  })?.length || 0;

  // Lead metrics
  const totalLeads = leads?.length || 0;
  const leadsByStage = {
    new: leads?.filter(l => l?.stage === 'new')?.length || 0,
    contacted: leads?.filter(l => l?.stage === 'contacted')?.length || 0,
    qualified: leads?.filter(l => l?.stage === 'qualified')?.length || 0,
    proposal: leads?.filter(l => l?.stage === 'proposal')?.length || 0,
    negotiation: leads?.filter(l => l?.stage === 'negotiation')?.length || 0,
    won: leads?.filter(l => l?.stage === 'won')?.length || 0,
    lost: leads?.filter(l => l?.stage === 'lost')?.length || 0
  };
  const conversionRate = totalLeads > 0 ? ((leadsByStage?.won / totalLeads) * 100)?.toFixed(1) : 0;

  // Center metrics
  const totalCenters = centers?.length || 0;
  const totalSqft = centers?.reduce((sum, c) => sum + (c?.totalSqft || 0), 0);

  return {
    occupancy: {
      totalUnits,
      occupiedUnits,
      vacantUnits,
      occupancyRate
    },
    revenue: {
      totalRevenue,
      avgRentPerSqft
    },
    tenants: {
      activeTenants,
      expiringLeases
    },
    leads: {
      totalLeads,
      leadsByStage,
      conversionRate
    },
    centers: {
      totalCenters,
      totalSqft
    }
  };
}

/**
 * Generate AI chatbot response with real business context
 */
export async function generateChatbotResponse(userMessage, conversationHistory = []) {
  try {
    // Fetch real business data
    const businessData = await fetchBusinessContext();
    const metrics = calculateMetrics(businessData);

    // Build system prompt with real data context
    const systemPrompt = `You are an AI assistant for PropquesOS, a commercial real estate management platform. You help admins and managers analyze their business data and provide actionable insights.

Current Business Metrics:
- Occupancy: ${metrics?.occupancy?.occupiedUnits}/${metrics?.occupancy?.totalUnits} units (${metrics?.occupancy?.occupancyRate}% occupancy rate)
- Vacant Units: ${metrics?.occupancy?.vacantUnits}
- Active Tenants: ${metrics?.tenants?.activeTenants}
- Expiring Leases (Next 3 Months): ${metrics?.tenants?.expiringLeases}
- Total Revenue: $${metrics?.revenue?.totalRevenue?.toLocaleString()}
- Avg Rent per Sqft: $${metrics?.revenue?.avgRentPerSqft}
- Total Leads: ${metrics?.leads?.totalLeads}
- Lead Conversion Rate: ${metrics?.leads?.conversionRate}%
- Lead Pipeline: New (${metrics?.leads?.leadsByStage?.new}), Contacted (${metrics?.leads?.leadsByStage?.contacted}), Qualified (${metrics?.leads?.leadsByStage?.qualified}), Proposal (${metrics?.leads?.leadsByStage?.proposal}), Negotiation (${metrics?.leads?.leadsByStage?.negotiation}), Won (${metrics?.leads?.leadsByStage?.won}), Lost (${metrics?.leads?.leadsByStage?.lost})
- Total Centers: ${metrics?.centers?.totalCenters}
- Total Sqft: ${metrics?.centers?.totalSqft?.toLocaleString()}

Your role:
1. Answer questions about occupancy, revenue, tenants, leads, and business performance
2. Provide data-driven recommendations and insights
3. Identify trends, risks, and opportunities
4. Suggest actionable next steps
5. Be concise, professional, and data-focused

When asked about specific data, reference the actual numbers provided above. Provide context and analysis, not just raw numbers.`;

    // Build messages array with conversation history
    const messages = [
      { role: 'system', content: systemPrompt },
      ...conversationHistory,
      { role: 'user', content: userMessage }
    ];

    // Get AI response
    const response = await getChatCompletion(
      AI_PROVIDER,
      AI_MODEL,
      messages,
      { max_completion_tokens: 1500 }
    );

    const content = response?.choices?.[0]?.message?.content;
    if (!content || typeof content !== 'string') {
      console.error('Invalid AI response structure:', response);
      throw new Error('AI service returned invalid response format');
    }
    
    return {
      content,
      metrics,
      timestamp: new Date()?.toISOString()
    };
  } catch (error) {
    console.error('Generate chatbot response error:', error);
    throw error;
  }
}
